import classNames from 'classnames'

import { ButtonRoundSm, CheckboxInputSM, ControlledInputDebounced, SelectInputSM } from '@apps-common/components/elements'
import { ModalBody, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { INPUT_CLASSES, INPUT_CONTAINER_CLASSES } from '@apps-common/config/input-styles'
import { ModalDismiss } from '@apps-common/config/modal'
import { UNIT_ORDER_VALUES, UNIT_STATUS_ACTIVE, UNIT_STATUS_VALUES } from '@apps-common/config/unit-filter'
import { usePortalUnitSelectItems } from '@apps-common/hooks/usePortalUnitSelectItems'
import { usePortalUnitsFilterStore } from '@apps-common/stores'
import { IPortalUnitFilterDefaults } from '@apps-common/types/portal/unit'
import { FilterModalContainer } from './filter/FilterModalContainer'

export interface IManagementUnitsFilterModal {
  defaults?: IPortalUnitFilterDefaults
}

export const PortalUnitsFilterModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IManagementUnitsFilterModal
}) => {
  const state = usePortalUnitsFilterStore()
  const filterDefaults = componentProps?.defaults?.filter

  const { isLoading, monitoringCentres, bureaus, technicians, networkTypes } = usePortalUnitSelectItems()

  const loading = isLoading

  return (
    <FilterModalContainer onDismiss={onDismiss} classes='sm:!max-w-[500px] sm:!h-auto' useFullHeight>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle='Portal devices' title='Filter' subtitleClasses='capitalize' />
          <div className='flex items-center gap-[10px]'>
            <ButtonRoundSm
              type='button'
              className='action-brand'
              callback={() => {
                state.clear()
                onDismiss(null, ModalDismiss.Confirm)
              }}
            >
              <commonIcons.reload />
            </ButtonRoundSm>
            <ButtonRoundSm
              type='button'
              className='action-brand-solid'
              callback={() => {
                onDismiss(null, ModalDismiss.Cancel)
              }}
            >
              <commonIcons.close />
            </ButtonRoundSm>
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[100px] !pb-[30px] sm:!pb-[20px]'>
        <div className='flex flex-col gap-2'>
          <div className='grid sm:grid-cols-2 sm:gap-y-2 sm:gap-x-3'>
            {/* SITE NAME */}
            {!filterDefaults?.hideSite && (
              <ControlledInputDebounced
                type='text'
                placeholder='Site name'
                label='Site name'
                showLabel
                id='unit-management-filter-site-name'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                value={state.site}
                disabled={loading || filterDefaults?.disableSite}
                onChange={(e) => state.setSite(e?.target?.value || '')}
              />
            )}
            {/* ACCOUNT NO */}
            {!filterDefaults?.hideAccountNo && (
              <ControlledInputDebounced
                type='text'
                placeholder='Account number'
                label='Account number'
                showLabel
                id='unit-management-filter-account-number'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                value={state.accountNo}
                disabled={loading || filterDefaults?.disableAccountNo}
                onChange={(e) => state.setAccountNo(e?.target?.value || '')}
              />
            )}

            {/* MOBILE NO */}
            {!filterDefaults?.hideMobileNo && (
              <ControlledInputDebounced
                type='text'
                placeholder='Mobile number'
                label='Mobile number'
                showLabel
                id='unit-management-filter-mobile-number'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                value={state.mobileNo}
                disabled={loading || filterDefaults?.disableMobileNo}
                onChange={(e) => state.setMobileNo(e?.target?.value || '')}
              />
            )}
            {/* IMEI */}
            {!filterDefaults?.hideImei && (
              <ControlledInputDebounced
                type='text'
                placeholder='IMEI number'
                label='IMEI number'
                showLabel
                id='unit-management-filter-imei-number'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                value={state.imei}
                disabled={loading || filterDefaults?.disableImei}
                onChange={(e) => state.setImei(e?.target?.value || '')}
              />
            )}
            {/* SERIAL NO */}
            {!filterDefaults?.hideSerialNo && (
              <ControlledInputDebounced
                type='text'
                placeholder='Serial number'
                label='Serial number'
                showLabel
                id='unit-management-filter-serial-number'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                value={state.serialNo}
                disabled={loading || filterDefaults?.disableSerialNo}
                onChange={(e) => state.setSerialNo(e?.target?.value || '')}
              />
            )}
            {/* UNIT STATUS */}
            {!filterDefaults?.hideStatus && (
              <SelectInputSM
                label='Status'
                showLabel
                id='unit-management-filter-status'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                options={UNIT_STATUS_VALUES}
                loading={loading}
                disabled={filterDefaults?.disableStatus}
                hideError
                registration={{ value: state.status, onChange: (e: any) => state.setStatus(e?.target?.value || '') }}
              />
            )}
            {/* MONITORING CENTRE */}
            {!filterDefaults?.hideMonitoringCentre && !state.pinnedUnits && (
              <SelectInputSM
                label='Monitoring Centre'
                showLabel
                id='unit-management-filter-monitoring-centre'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                options={[{ label: 'All Centres', value: '' }, ...monitoringCentres.options]}
                loading={loading}
                disabled={filterDefaults?.disableMonitoringCentre}
                hideError
                registration={{ value: state.mCentreId, onChange: (e: any) => state.setMCentreId(e?.target?.value || '') }}
              />
            )}
            {/* BUREAU */}
            {!filterDefaults?.hideBureau && !state.pinnedUnits && (
              <SelectInputSM
                label='Bureau'
                showLabel
                id='unit-management-filter-bureau'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                options={[{ label: 'All Bureaus', value: '' }, ...bureaus.options]}
                loading={loading}
                disabled={filterDefaults?.disableBureau}
                hideError
                registration={{ value: state.bureauId, onChange: (e: any) => state.setCompanyId(e?.target?.value || '') }}
              />
            )}
            {/* TECHNICIAN */}
            {!filterDefaults?.hideTech && !state.pinnedUnits && (
              <SelectInputSM
                label='Technician'
                showLabel
                id='unit-management-filter-technician'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                options={[{ label: 'All Technicians', value: '' }, ...technicians.options]}
                loading={loading}
                disabled={filterDefaults?.disableTech}
                hideError
                registration={{ value: state.techCompanyId, onChange: (e: any) => state.setTechCompanyId(e?.target?.value || '') }}
              />
            )}
            {/* NETWORK TYPES */}
            {!filterDefaults?.hideNetworkType && (
              <SelectInputSM
                label='Network'
                showLabel
                id='unit-management-filter-network-type'
                className={INPUT_CLASSES}
                containerClassName={INPUT_CONTAINER_CLASSES}
                options={[{ label: 'All Networks', value: '' }, ...networkTypes.options]}
                loading={loading}
                disabled={filterDefaults?.disableNetworkType}
                hideError
                registration={{ value: state.networkType, onChange: (e: any) => state.setNetworkType(e?.target?.value || '') }}
              />
            )}
          </div>
          {/* ORDER BY */}
          {!filterDefaults?.hideSortBy && (
            <SelectInputSM
              placeholder='sort by'
              label='Sort by'
              showLabel
              id='unit-filter-sort-by'
              className={INPUT_CLASSES}
              containerClassName={classNames(INPUT_CONTAINER_CLASSES)}
              options={UNIT_ORDER_VALUES.filter((o) => !o.valueCheck || o.valueCheck === state.status)}
              loading={loading}
              disabled={filterDefaults?.disableSortBy}
              registration={{ value: state.orderBy, onChange: (e: any) => state.setOrderBy(e?.target?.value || '') }}
            />
          )}

          {state.status === UNIT_STATUS_ACTIVE.value && !filterDefaults?.hidePinnedUnits && (
            <CheckboxInputSM
              label='Pinned devices'
              registration={{
                checked: state.pinnedUnits,
                onChange: (e: any) => {
                  const value = e?.target?.checked

                  if (value) {
                    state.setMCentreId('')
                    state.setCompanyId('')
                    state.setTechCompanyId('')
                  }
                  state.setPinnedUnits(value || false)
                }
              }}
              loading={loading}
              disabled={filterDefaults?.disablePinnedUnits}
              containerClassName='sm:col-span-2'
            />
          )}
        </div>
      </ModalBody>
    </FilterModalContainer>
  )
}
